import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { fetchCollection, getLocalized } from '../lib/cms'
import { updateMeta } from '../lib/utils'

import TrustBar from '../components/TrustBar'
import FAQSection from '../components/FAQSection'
import ContactForm from '../components/ContactForm'
import { 
  Check, 
  ArrowRight, 
  ShieldCheck, 
  Sparkles, 
  Phone, 
  Mail, 
  Clock, 
  HelpCircle,
  FileCheck,
  CheckCircle2
} from 'lucide-react'

const getServicePathForPlan = (planTitle) => {
  const title = typeof planTitle === 'object' ? planTitle.da : planTitle
  if (title?.includes('Årsopgørelse') || title?.includes('Forskudsopgørelse')) {
    return '/ydelser/personlig-selvangivelse'
  }
  if (title === 'Årsregnskab') {
    return '/aarsrapport-og-aarsregnskab'
  }
  if (title?.includes('bogføring') || title?.includes('regnskab og bogføring')) {
    return '/regnskabsservice'
  }
  return null
}

export default function PricingPage() {
  const [pricingPlans, setPricingPlans] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    updateMeta(
      "Priser på revisor og bogføring | Faste priser uden overraskelser | Thellefsen Regnskab",
      "Se vores faste og gennemskuelige priser på årsregnskab, bogføring og skat. Årsregnskab fra 4.000 kr. ex moms. Gratis regnskabsprogram inkluderet."
    )

    async function loadPricing() {
      try {
        const data = await fetchCollection('pricing_plans')
        
        // Sortering: 1. Årsregnskab, 2. Årsregnskab og bogføring, 3. Årsopgørelse & Forskudsopgørelse
        const orderMap = {
          'Årsregnskab': 1,
          'Årsregnskab og bogføring': 2,
          'Årsopgørelse & Forskudsopgørelse': 3
        }
        
        const sorted = [...data].sort((a, b) => {
          const titleA = getLocalized(a.title)
          const titleB = getLocalized(b.title)
          return (orderMap[titleA] || 99) - (orderMap[titleB] || 99)
        })
        
        setPricingPlans(sorted)
      } catch (error) {
        console.error('Fejl ved hentning af priser:', error)
      } finally {
        setLoading(false)
      }
    }

    loadPricing()
  }, [])

  const faqItems = [
    {
      question: "Hvad koster det at få lavet et årsregnskab?",
      answer: "Vores faste pris for udarbejdelse af årsregnskab starter ved 4.000 kr. ex moms for både enkeltmandsvirksomheder og ApS. Prisen inkluderer udarbejdelse af regnskabet, indberetning til Erhvervsstyrelsen (for ApS), indberetning af virksomhedens selvangivelse til Skattestyrelsen samt eventuel udbytteindberetning."
    },
    {
      question: "Hvad koster den samlede pakke med både bogføring og årsregnskab?",
      answer: "Vores mest populære samlede regnskabspakke koster 8.000 kr. ex moms om året. Den dækker løbende bogføring af op til 200 bilag, bankafstemning, momsindberetning, gratis digitalt regnskabsprogram samt det fulde årsregnskab og selvangivelser."
    },
    {
      question: "Hvordan fungerer det gratis regnskabsprogram?",
      answer: "Vælger jeres virksomhed Thellefsen Regnskab, er der i prisen inkluderet et professionelt, digitalt regnskabsprogram. Det betyder, at du sparer de normale abonnementsomkostninger (ca. 4.000 kr. om året) og samtidig er 100% compliant med kravene i den nye Bogføringslov."
    },
    {
      question: "Hvad hvis min virksomhed har mere end 200 bilag om året?",
      answer: "Har din virksomhed et større bilagsflow end 200 bilag, sammensætter vi blot en individuel og fast årlig aftale tilpasset jeres nøjagtige volumen. Vi gennemgår altid dit behov forud, så prisen forbliver fast og forudsigelig uden overraskelser."
    },
    {
      question: "Er der skjulte gebyrer eller uventede timefakturaer?",
      answer: "Nej. Vi arbejder ud fra et princip om fuld gennemsigtighed. Den aftalte pris er den pris, du betaler. Mindre opkald, løbende rådgivning og faste deadlines er tænkt ind, så du aldrig skal tøve med at ringe til din revisor."
    },
    {
      question: "Hvordan skifter jeg til Thellefsen Regnskab?",
      answer: "Det er ganske enkelt. Vi hjælper med hele overdragelsen fra din tidligere revisor eller bogholder, herunder indhentning af tidligere saldobalance, stamdata og bilag. Du behøver ikke bekymre dig om det praktiske."
    }
  ]

  return (
    <div className="bg-stone-50 text-stone-800 min-h-screen">
      {/* Hero sektion */}
      <section className="bg-white border-b border-stone-200 pt-16 pb-14 lg:pt-20 lg:pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-orange-50 border border-orange-200 text-xs font-semibold uppercase tracking-wider text-orange-700">
              <ShieldCheck className="h-3.5 w-3.5 text-orange-600" />
              Gennemskuelige revisorpriser
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 tracking-tight">
              Faste, gennemskuelige priser – uden overraskelser
            </h1>
            <p className="text-lg sm:text-xl text-stone-600 leading-relaxed pt-2">
              Få en fast og personlig revisor med mere end 10 års erfaring, som følger din virksomhed hele vejen. 
              Du får den samme kontaktperson år efter år, til en fast årlig pris på 4.000–8.000 kr. ex moms uden overraskelser.
            </p>
          </div>

          {/* Fremhævet fordel boks: Gratis regnskabsprogram & Bogføringslov */}
          <div className="mt-10 max-w-3xl mx-auto bg-stone-50 border-l-4 border-orange-600 p-5 sm:p-6 shadow-sm">
            <div className="flex items-start gap-3.5">
              <div className="mt-0.5 rounded-full bg-orange-100 p-1 text-orange-700 shrink-0">
                <Check className="h-4 w-4 stroke-[3]" />
              </div>
              <p className="text-sm sm:text-base font-medium text-stone-800 leading-relaxed">
                Vælger jeres virksomhed Thellefsen Regnskab er der i prisen inkluderet gratis regnskabsprogram. 
                Det betyder, at jeres virksomhed overholder den nye Bogføringslov og får en besparelse på ca. 4.000 kr. om året.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <TrustBar />

      {/* Hoved prissektion med priskort */}
      <section className="py-16 sm:py-24 border-b border-stone-200 bg-[#f8f8f8]" id="priser">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="block text-xs sm:text-sm font-bold uppercase tracking-widest text-[#eb5a28] mb-3">
              Gennemskuelige priser
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#121212] tracking-tight leading-tight mb-5">
              Ingen ubehagelige overraskelser.<br className="hidden sm:inline" /> Kun faste priser.
            </h2>
            <p className="max-w-2xl mx-auto text-base sm:text-lg text-[#666666] leading-relaxed">
              Vi tror på fuld gennemsigtighed. Derfor arbejder vi med faste, aftalte priser, så du altid ved præcis, hvad din revisor koster.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto gap-8">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-96 rounded-none bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 max-w-4xl mx-auto gap-8 items-stretch">
              {pricingPlans.map((plan) => {
                const titleText = getLocalized(plan.title)
                const isPopular = false

                return (
                  <div 
                    key={plan.id}
                    className="relative w-full rounded-none border p-8 bg-white flex flex-col justify-between transition-all duration-300 shadow-sm hover:shadow-md border-stone-200"
                  >
                    <div>
                      <h3 className="text-2xl font-bold text-stone-900">
                        {titleText}
                      </h3>
                      <p className="text-xs text-stone-500 mt-1.5 min-h-[28px]">
                        {getLocalized(plan.description)}
                      </p>
                      
                      <div className="mt-6 border-b border-stone-100 pb-6">
                        <span className="text-3xl font-bold text-stone-900 tracking-tight">
                          {plan.price}
                        </span>
                      </div>

                      {/* Features liste */}
                      <div 
                        className="mt-6 text-sm text-stone-600 space-y-3 prose prose-sm max-w-none prose-ul:list-none prose-ul:pl-0 prose-li:relative prose-li:before:content-['✓'] prose-li:before:absolute prose-li:before:left-0 prose-li:before:text-orange-600 prose-li:before:font-bold prose-li:pl-6"
                        dangerouslySetInnerHTML={{ __html: getLocalized(plan.features) }}
                      />
                    </div>

                    <div className="mt-8 pt-6 border-t border-stone-100 flex flex-col gap-3">
                      {getServicePathForPlan(plan.title) && (
                        <Link
                          to={getServicePathForPlan(plan.title)}
                          className="w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-orange-600 hover:text-orange-700 transition-colors py-1"
                        >
                          Læs mere om ydelsen
                          <ArrowRight className="h-3 w-3" />
                        </Link>
                      )}
                      <Link
                        to={`/kontakt?type=pris&plan=${encodeURIComponent(titleText)}`}
                        className="w-full inline-flex items-center justify-center rounded-none px-4 py-3 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md bg-stone-900 text-white hover:bg-orange-600"
                      >
                        Vælg denne løsning
                      </Link>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>



      {/* Hvorfor faste priser hos Thellefsen Regnskab */}
      <section className="py-16 sm:py-20 bg-stone-100/60 border-b border-stone-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
              Tryghed & gennemsigtighed
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
              Derfor vælger selvstændige Thellefsen Regnskab
            </h2>
            <p className="text-stone-600 leading-relaxed">
              Vi fjerner usikkerheden om regningen og sikrer dig en personlig samarbejdspartner, der kender din virksomhed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white border border-stone-200 p-6 shadow-sm">
              <div className="w-10 h-10 bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center font-bold mb-4">
                <CheckCircle2 className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Ingen overraskelser
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Aftalt fast pris betyder fast pris. Du modtager ikke pludselige ekstraregninger for korte opkald eller almindelige regnskabsspørgsmål.
              </p>
            </div>

            <div className="bg-white border border-stone-200 p-6 shadow-sm">
              <div className="w-10 h-10 bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center font-bold mb-4">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Gratis regnskabsprogram
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Spar ca. 4.000 kr. om året på software. Programmet lever fuldt ud op til den nye Bogføringslovs krav til digital bilagsopbevaring.
              </p>
            </div>

            <div className="bg-white border border-stone-200 p-6 shadow-sm">
              <div className="w-10 h-10 bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center font-bold mb-4">
                <Clock className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Fast kontaktperson
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Du bliver ikke sendt rundt mellem skiftende revisorassistenter. Du taler altid direkte med Peter Thellefsen.
              </p>
            </div>

            <div className="bg-white border border-stone-200 p-6 shadow-sm">
              <div className="w-10 h-10 bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center font-bold mb-4">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-bold text-stone-900 mb-2">
                Let revisorskifte
              </h3>
              <p className="text-sm text-stone-600 leading-relaxed">
                Vi hjælper med at hente al nødvendig historik fra din nuværende revisor eller bogholder, så overgangen sker gnidningsfrit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ sektion */}
      <FAQSection 
        title="Spørgsmål om vores priser & vilkår"
        subtitle="Her finder du svar på de mest almindelige spørgsmål omkring betaling, bilag og aftaler."
        items={faqItems}
        bg="bg-white"
      />

      {/* Kontakt / Tilbud sektion */}
      <section id="tilbud" className="py-16 sm:py-20 bg-stone-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 space-y-6">
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">
                Uforpligtende prisoverslag
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">
                Få et fast tilbud tilpasset din virksomhed
              </h2>
              <p className="text-stone-600 leading-relaxed">
                Udfyld formularen, eller giv Peter et uforpligtende ring. Vi vender tilbage inden for 24 timer med en fast pris og en klar plan.
              </p>

              <div className="space-y-4 pt-4 border-t border-stone-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-none bg-white border border-stone-200 flex items-center justify-center text-stone-900 shrink-0">
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-stone-500 font-medium">Ring direkte</div>
                    <a href="tel:+4531335470" className="text-base font-bold text-stone-900 hover:text-orange-600 transition-colors">
                      31 33 54 70
                    </a>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-none bg-white border border-stone-200 flex items-center justify-center text-stone-900 shrink-0">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-xs text-stone-500 font-medium">Send en e-mail</div>
                    <a href="mailto:info@tregnskab.dk" className="text-base font-bold text-stone-900 hover:text-orange-600 transition-colors">
                      info@tregnskab.dk
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-stone-200 p-5 space-y-2 mt-6">
                <div className="text-sm font-semibold text-stone-900">
                  Hurtig afklaring på 15 minutter
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Er du i tvivl om, hvilken pakke din virksomhed hører under? Vi tager gerne en kort telefonsamtale og gennemgår din situation.
                </p>
              </div>
            </div>

            <div className="lg:col-span-7 bg-white border border-stone-200 p-6 sm:p-8 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
